import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { GridColDef } from "@mui/x-data-grid-premium";
import { CircularProgress, IconButton, Paper, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PatientDatas from "src/modules/medcard/patient-datas";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import PatientMonitoringCreate from "../create";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { referenceService } from "src/api/references/referenceService";
import { getCardData } from "src/utils/card/cardData";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import {
  CreatePatientMonitoringRequestDto,
  GetPatientMonitoringListResponseDto,
  UpdatePatientMonitoringRequestDto,
} from "src/api/references/referenceServicesDtos";
import { formatDateTime, toLocalISO } from "src/utils/date";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import ByzNursePrintButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";

function PatientMonitoringPatientList() {
  const { t } = useTranslate();
  const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
  const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
  const selectedPatientId = mainTabs[activeTabIndex]?.data?.patientId;

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<string | undefined>();
  const [formData, setFormData] = useState<any>({});

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [patientInfo, setPatientInfo] = useState<any>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const isFetchedOnce = useRef(false);

  const cardData = getCardData("PATIENT_MONITORING");

  const showAlert = (msg: string, type: "success" | "error") => {
    setAlertMessage(msg);
    setAlertType(type);
    setAlertOpen(true);
  };

  const buildFormData = useCallback((row?: any) => ({
    ID: row?.ID ? String(row.ID) : "",
    CUSER: row?.cuser ?? "",
    CTIME: row?.ctimeRaw ? new Date(row.ctimeRaw) : null,
    PTIME: row?.ptimeRaw ? new Date(row.ptimeRaw) : null,
    NOTE: row?.note ?? "",
    WRITE_ON_EPICRISE: row?.write_on_epicrise ?? "0",
    PT_ID: selectedPatientId ? String(selectedPatientId) : "",
    NURSE_ID: "",
  }), [selectedPatientId]);

  const loadData = async () => {
    if (!selectedPatientId) return;
    if (isFetchedOnce.current) return;

    isFetchedOnce.current = true;
    setFetchLoading(true);

    try {
      const response: GetPatientMonitoringListResponseDto =
        await referenceService.patient.GetPatientMonitoring({
          PtId: Number(selectedPatientId),
        });

      if (Array.isArray(response)) {
        const formattedRows = response.map((item) => ({
          id: item.id,
          ID: item.id,
          ctime: formatDateTime(item.ctime),
          ctimeRaw: item.ctime,
          ptime: item.ptime,
          ptimeRaw: item.ptime,
          cuser: item.cuser || "",
          note: item.note || "",
          write_on_epicrise: item.write_on_epicrise || "",
          isDoctor: item.isdoctor,
          canEdit: item.canedit,
          canDelete: item.candelete,
        }));
        setRows(formattedRows);
      }
    } catch (err) {
      console.error(err);
      showAlert(t("error-loading-data"), "error");
    } finally {
      setFetchLoading(false);
    }
  };

  const doctorRows = useMemo(() => rows.filter(r => r.isDoctor), [rows]);
  const nurseRows = useMemo(() => rows.filter(r => !r.isDoctor), [rows]);

  useEffect(() => {
    loadData();
  }, []);

  const handleAdd = () => {
    setSelectedRecordId(undefined);
    setFormData(buildFormData());
    setTitle(t("patient-monitoring.add-new"));
    setOpen(true);
  };

  const handleEdit = (row: any) => {
    setFormData(buildFormData(row));
    setSelectedRecordId(String(row.ID ?? row.id));
    setTitle(t("patient-monitoring.title") + " " + t("edit", 'capitalize'));
    setOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteItemId(String(id));
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItemId) return;

    setDeleteLoading(deleteItemId);
    try {
      await referenceService.patient.DeletePatientMonitoring(Number(deleteItemId));
    } catch (err) {
      console.error(err);
      showAlert(t("delete-failed"), "error");
    } finally {
      setDeleteLoading(null);
      setDeleteDialogOpen(false);
      setDeleteItemId(null);
    }

    isFetchedOnce.current = false;
    await loadData();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteItemId(null);
    setDeleteLoading(null);
  };

  const handleSave = async () => {
    if (!selectedPatientId) return;

    if (!formData.NOTE || formData.NOTE.trim() === "") {
      showAlert(t("patient-monitoring.note-required"), "error");
      return;
    }

    setSaveLoading(true);
    try {
      if (selectedRecordId) {
        const payload: UpdatePatientMonitoringRequestDto = {
          note: formData.NOTE ?? "",
          write_on_epicrise: formData.WRITE_ON_EPICRISE ?? null,
          ptime: toLocalISO(formData.PTIME),
        };

        await referenceService.patient.UpdatePatientMonitoring(
          Number(selectedRecordId),
          payload
        );

        showAlert(t("update-success"), "success");
      } else {
        const payload: CreatePatientMonitoringRequestDto = {
          pt_id: Number(selectedPatientId),
          note: formData.NOTE ?? "",
          write_on_epicrise: formData.WRITE_ON_EPICRISE ?? "0",
          ptime: toLocalISO(formData.PTIME),
        };

        await referenceService.patient.AddPatientMonitoring(payload);

        showAlert(t("save-success"), "success");
      }

      setOpen(false);

      isFetchedOnce.current = false;
      await loadData();
    } catch (err: any) {
      console.error(err);
      const errorMessage = err?.error || err?.response?.data?.error;
      if (errorMessage) {
        showAlert(errorMessage, "error");
      } else {
        showAlert(t("save-error"), "error");
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFormChange = useCallback((field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  }, []);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "update",
        headerName: t("card.update"),
        width: 120,
        renderCell: (params) => (
          <IconButton
            onClick={() => handleEdit(params.row)}
            disabled={!params.row.canEdit}
          >
            <EditIcon color={params.row.canEdit ? "info" : "disabled"} />
          </IconButton>
        ),
      },
      {
        field: "ptime",
        headerName: t("patient-monitoring.ptime"),
        flex: 1,
        valueFormatter: (value) => formatDateTime(value),
      },
      { field: "cuser", headerName: t("patient-monitoring.created-by"), flex: 1 },
      { field: "note", headerName: t("patient-monitoring.note"), flex: 2 },
      {
        field: "write_on_epicrise", headerName: t("patient-monitoring.write-on-epicrise"), flex: 1,
        renderCell: (params) => (
          <ByzMuiCheckBox checked={params.value === "1"} disabled circular={true} />
        ),
      },
      {
        field: "delete",
        headerName: t("card.delete"),
        width: 120,
        renderCell: (params) => (
          deleteLoading === String(params.row.ID) ? (
            <CircularProgress size={20} color="error" />
          ) : (
            <ByzGrdDeleteButton
              onClick={() => handleDeleteClick(params.row.ID)}
              disabled={!params.row.canDelete}
            />
          )
        ),
      },
    ],
    [t, deleteLoading]
  );

  return (
    <>
      <PatientDatas patientDataOut={(data) => {
        setPatientInfo(data.data);
      }} />

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        patientId={deleteItemId?.toString() || ""}
        title={t("card.delete-confirmation")}
        content={t("card.delete-confirmation-message")}
        onConfirm={handleDeleteConfirm}
        successMessage={t("card.record-deleted-successfully")}
        errorMessage={t("card.delete-failed")}
      />

      <Stack mt={5} direction="row" spacing={2}>
        <ByzMuiAddButton onClick={handleAdd} popup={true} />
        <ByzNursePrintButton disabled />
      </Stack>

      <Paper sx={{ px: 4, pb: 4, mt: 2, borderRadius: "16px" }}>
        <Stack direction="row" alignItems="center" sx={{ pt: 3 }}>
          <ByzMuiTypography sx={{ fontSize: "18px", fontWeight: 700 }}>
            {t("patient-monitoring.doctor-monitoring")}
          </ByzMuiTypography>
        </Stack>
        <ByzMuiDataGrid
          customToolbar={() => <ByzDataGridToolbar />}
          columns={columns}
          rows={doctorRows}
          whiteGrid
          loading={fetchLoading}
        />
      </Paper>

      <Paper sx={{ px: 4, pb: 4, mt: 2, borderRadius: "16px" }}>
        <Stack direction="row" alignItems="center" sx={{ pt: 3 }}>
          <ByzMuiTypography sx={{ fontSize: "18px", fontWeight: 700 }}>
            {t("patient-monitoring.nurse-monitoring")}
          </ByzMuiTypography>
        </Stack>
        <ByzMuiDataGrid
          customToolbar={() => <ByzDataGridToolbar />}
          columns={columns}
          rows={nurseRows}
          whiteGrid
          loading={fetchLoading}
        />
      </Paper>

      <OpenComponentDialog
        title={title}
        open={open}
        onClose={() => setOpen(false)}
        isMedCard
      >
        <Stack sx={{ mb: 2 }} direction="row">
          <ByzMuiSaveButton onClick={handleSave} loading={saveLoading} />
        </Stack>

        {open && (
          <PatientMonitoringCreate
            recordId={selectedRecordId}
            patientId={String(selectedPatientId)}
            onClose={() => setOpen(false)}
            onSave={loadData}
            onChange={handleFormChange}
            formData={formData}
          />
        )}
      </OpenComponentDialog>

      <ByzMuiSnackBar
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title={alertType === "success" ? t("success") : t("error")}
        message={alertMessage}
        type={alertType}
      />
    </>
  );
}

export default PatientMonitoringPatientList;
